import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface TestPoint {
    lat: number;
    lng: number;
    inside: boolean;
}

interface GeofenceMapProps {
    /** Geometría de la geocerca en GeoJSON (FeatureCollection) como string. */
    geojson?: string | null;
    /** Punto de marca a validar, dibujado sobre el mapa. */
    testPoint?: TestPoint | null;
    /** Ancho del corredor en metros (se dibuja como círculo de tolerancia alrededor del punto). */
    bufferMeters?: number;
    /** Altura del mapa. */
    height?: number | string;
}

/**
 * Mapa Leaflet que previsualiza la geocerca de un proyecto (polígono / trazado / puntos) y, opcionalmente,
 * el punto de un marcaje validado. Se usa Leaflet directamente (no react-leaflet) para evitar problemas de
 * empaquetado ESM con Create React App.
 */
const GeofenceMap: React.FC<GeofenceMapProps> = ({ geojson, testPoint, bufferMeters, height = 460 }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const geoLayerRef = useRef<L.GeoJSON | null>(null);
    const pointLayerRef = useRef<L.LayerGroup | null>(null);

    // Inicializa el mapa una sola vez.
    useEffect(() => {
        if (mapRef.current || !containerRef.current) {
            return;
        }
        const map = L.map(containerRef.current).setView([-30.5, -71.4], 11); // Coquimbo, Chile (por defecto)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
            maxZoom: 19,
        }).addTo(map);
        pointLayerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        // Al montarse dentro de contenedores que cambian de tamaño (tabs), forzar recálculo.
        // El temporizador se cancela en el cleanup para no ejecutarse tras destruir el mapa
        // (React StrictMode monta/desmonta dos veces en desarrollo, lo que provocaba el error _leaflet_pos).
        const resizeTimer = setTimeout(() => {
            if (mapRef.current) {
                mapRef.current.invalidateSize();
            }
        }, 300);

        return () => {
            clearTimeout(resizeTimer);
            map.remove();
            mapRef.current = null;
            pointLayerRef.current = null;
            geoLayerRef.current = null;
        };
    }, []);

    // Dibuja la geometría de la geocerca cuando cambia.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        if (geoLayerRef.current) {
            geoLayerRef.current.remove();
            geoLayerRef.current = null;
        }
        if (!geojson) {
            return;
        }
        try {
            const data = JSON.parse(geojson);
            const layer = L.geoJSON(data, {
                style: () => ({ color: '#0056b3', weight: 3, fillColor: '#0056b3', fillOpacity: 0.12 }),
                pointToLayer: (_feature, latlng) =>
                    L.circleMarker(latlng, {
                        radius: 4,
                        color: '#0056b3',
                        fillColor: '#0056b3',
                        fillOpacity: 0.9,
                    }),
            }).addTo(map);
            geoLayerRef.current = layer;
            const bounds = layer.getBounds();
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [30, 30] });
            }
        } catch (e) {
            // GeoJSON inválido: no se dibuja nada.
        }
    }, [geojson]);

    // Dibuja el punto de marca validado (verde = dentro, rojo = fuera) y su círculo de tolerancia.
    useEffect(() => {
        const map = mapRef.current;
        const group = pointLayerRef.current;
        if (!map || !group) {
            return;
        }
        group.clearLayers();
        if (!testPoint) {
            return;
        }
        const color = testPoint.inside ? '#2e7d32' : '#c62828';
        L.circleMarker([testPoint.lat, testPoint.lng], {
            radius: 8,
            color,
            fillColor: color,
            fillOpacity: 0.9,
        }).addTo(group);
        if (bufferMeters && bufferMeters > 0) {
            L.circle([testPoint.lat, testPoint.lng], {
                radius: bufferMeters,
                color,
                weight: 1,
                fillColor: color,
                fillOpacity: 0.08,
            }).addTo(group);
        }
        map.setView([testPoint.lat, testPoint.lng], Math.max(map.getZoom(), 13));
    }, [testPoint, bufferMeters]);

    return <div ref={containerRef} style={{ height, width: '100%', borderRadius: 8 }} />;
};

export default GeofenceMap;
